import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  clearLocalSession,
  clearSession,
  fetchAccount,
  getStoredSession,
  login as apiLogin,
  logoutEverywhere,
  purchaseSpins as apiPurchaseSpins,
  readCrossSessionEmail,
  recordSpin as apiRecordSpin,
  register as apiRegister,
  storeSession
} from '../api/backend';

const AuthContext = createContext();

// Normalize the various backend payload shapes (register/login vs. /me) into a
// single account object the UI can rely on.
const normalizeAccount = (data) => {
  if (!data) {
    return null;
  }
  const wheel = data.bitWheel || {};
  return {
    email: data.email,
    gamerTag: data.gamerTag || '',
    signupDate: data.signupDate || null,
    subscriptionLevel: data.subscriptionLevel || 'free',
    spinsRemaining: data.spinsRemaining ?? wheel.spinsRemaining ?? 0,
    totalSpins: data.totalSpins ?? wheel.totalSpins ?? 0,
    points: data.points ?? wheel.points ?? 0,
    highScore: data.highScore ?? wheel.highScore ?? 0,
    gamesPlayed: data.gamesPlayed ?? wheel.gamesPlayed ?? 0,
    bestSingleSpinPayout: data.bestSingleSpinPayout ?? wheel.bestSingleSpinPayout ?? 0,
    numberHits: data.numberHits ?? wheel.numberHits ?? 0,
    colorHits: data.colorHits ?? wheel.colorHits ?? 0,
    purchases: Array.isArray(data.purchases) ? data.purchases : []
  };
};

export const AuthProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  // Passed to the auth modal so gated actions can explain why it opened.
  const [authReason, setAuthReason] = useState(null);

  // On load, restore a persisted session and validate it against the backend.
  useEffect(() => {
    let cancelled = false;
    const { token: storedToken } = getStoredSession();

    if (!storedToken) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const data = await fetchAccount(storedToken);
        if (cancelled) {
          return;
        }
        setToken(storedToken);
        setAccount(normalizeAccount(data));
      } catch (error) {
        if (!cancelled) {
          clearSession();
          setToken(null);
          setAccount(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const applyAuthResult = useCallback((data) => {
    const nextToken = data.token;
    storeSession({ token: nextToken, email: data.email });
    setToken(nextToken);
    setAccount(normalizeAccount(data));
  }, []);

  const signup = useCallback(
    async (email, password) => {
      const data = await apiRegister(email, password);
      applyAuthResult(data);
      return data;
    },
    [applyAuthResult]
  );

  const signin = useCallback(
    async (email, password) => {
      const data = await apiLogin(email, password);
      applyAuthResult(data);
      return data;
    },
    [applyAuthResult]
  );

  // Adopt an externally issued session token (e.g. from Google sign-in):
  // persist it, validate it against the backend, and load the account.
  const signinWithToken = useCallback(async (newToken, email) => {
    storeSession({ token: newToken, email });
    const data = await fetchAccount(newToken);
    setToken(newToken);
    setAccount(normalizeAccount(data));
    return data;
  }, []);

  const signout = useCallback(() => {
    // Invalidate the account's cross-app tokens server-side and clear the
    // shared cookies — signs the user out of every Satoshi Advance app.
    logoutEverywhere(account?.email);
    clearSession();
    setToken(null);
    setAccount(null);
  }, [account]);

  // Global-logout watcher: the shared .satoshiadvance.com userEmail cookie
  // is the cross-app source of truth. If it's gone while we still hold a
  // session, another app logged the user out — sign this tab out too
  // (locally only; the cookies are already cleared). Checked when the tab
  // regains focus and on a slow poll.
  useEffect(() => {
    if (!token) {
      return undefined;
    }
    const syncWithSharedCookie = () => {
      if (window.location.hostname === 'localhost') {
        return; // no shared cookie in local dev
      }
      if (!readCrossSessionEmail()) {
        clearLocalSession();
        setToken(null);
        setAccount(null);
      }
    };
    const onVisible = () => {
      if (!document.hidden) syncWithSharedCookie();
    };
    window.addEventListener('focus', syncWithSharedCookie);
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(syncWithSharedCookie, 30000);
    return () => {
      window.removeEventListener('focus', syncWithSharedCookie);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [token]);

  // Refresh the account snapshot (balance + purchase history) from Mongo.
  const refreshAccount = useCallback(async () => {
    if (!token) {
      return null;
    }
    try {
      const data = await fetchAccount(token);
      setAccount(normalizeAccount(data));
      return data;
    } catch (error) {
      if (error.status === 401) {
        signout();
      }
      return null;
    }
  }, [token, signout]);

  // Trust-based purchase: credit spins on the backend, then reflect the new
  // balance + purchase in the UI immediately from the response (no refresh).
  const purchaseSpins = useCallback(
    async (payload) => {
      if (!token) {
        throw new Error('Please log in to buy spins.');
      }
      const result = await apiPurchaseSpins(payload, token);
      setAccount((prev) =>
        prev
          ? {
              ...prev,
              spinsRemaining: result.spinsRemaining ?? prev.spinsRemaining,
              totalSpins: result.totalSpins ?? prev.totalSpins,
              points: result.points ?? prev.points,
              purchases: Array.isArray(result.purchases) ? result.purchases : prev.purchases
            }
          : prev
      );
      return result;
    },
    [token]
  );

  // Consume one spin on the backend (Phase 2 groundwork) and reflect the new
  // balance/points in the UI. Returns the result, or null if not logged in.
  const recordSpin = useCallback(
    async (pointsEarned = 0) => {
      if (!token) {
        return null;
      }
      try {
        const result = await apiRecordSpin({ pointsEarned }, token);
        setAccount((prev) =>
          prev
            ? {
                ...prev,
                spinsRemaining: result.spinsRemaining ?? prev.spinsRemaining,
                totalSpins: result.totalSpins ?? prev.totalSpins,
                points: result.points ?? prev.points
              }
            : prev
        );
        return result;
      } catch (error) {
        if (error.status === 401) {
          signout();
        }
        return null;
      }
    },
    [token, signout]
  );

  const openAuth = useCallback((reason = null) => {
    setAuthReason(reason);
    setAuthOpen(true);
  }, []);

  const closeAuth = useCallback(() => {
    setAuthOpen(false);
    setAuthReason(null);
  }, []);

  const value = useMemo(
    () => ({
      account,
      token,
      loading,
      isAuthenticated: Boolean(token && account),
      spinsRemaining: account?.spinsRemaining ?? 0,
      points: account?.points ?? 0,
      purchases: account?.purchases ?? [],
      signup,
      signin,
      signinWithToken,
      signout,
      refreshAccount,
      purchaseSpins,
      recordSpin,
      authOpen,
      authReason,
      openAuth,
      closeAuth
    }),
    [
      account,
      token,
      loading,
      signup,
      signin,
      signinWithToken,
      signout,
      refreshAccount,
      purchaseSpins,
      recordSpin,
      authOpen,
      authReason,
      openAuth,
      closeAuth
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
